import React, { useContext, useEffect } from "react";

import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Pager } from "@components-shared/desktop/inputs";
import { Button } from "@components/buttons";
import { RawFilterData } from "@data-types";
import {
	WithdrawalCustomPeriodInput,
	WithdrawalPaymentMethodFilterSection,
	WithdrawalPeriodsFilterSection,
	WithdrawalTransactionStatusFilterSection,
} from "@features/payment/withdrawal/desktop/my-withdrawals/filter";
import { MyWithdrawalsTable } from "@features/payment/withdrawal/desktop/my-withdrawals/table";
import { getUrlParamsAsObj } from "@state";
import { WithdrawalContext } from "@state/context/payment/WithdrawalContext";
import { MY_WITHDRAWALS_DEFAULT_FILTER } from "@state/stores/payment/withdrawal/WithdrawalFilterStore";
import { observer } from "mobx-react-lite";

import WithdrawalTabSwitcher from "../tabs/WithdrawalTabSwitcher";
import TransactionCancelConfirmModal from "./modal/TransactionCancelConfirmModal";

export default observer(function MyWithdrawalsBody() {
	const {
		disposeCurrentWithdrawalState,
		pageSize,
		totalItems,
		withdrawalFilterStore: {
			setRawFilterData,
			setDefaultFilterData,
			pageNumber,
			rawData,
		},
		isStoreInitialized,
		isLoadingProcess,
	} = useContext(WithdrawalContext);
	const methods = useForm();
	const { handleSubmit, reset } = methods;

	const { t } = useTranslation("payment");

	useEffect(() => {
		if (location.search !== "" && isStoreInitialized) {
			const params = getUrlParamsAsObj<RawFilterData>();
			reset(params);
			onSubmit(params);
		} else {
			setRawFilterData(MY_WITHDRAWALS_DEFAULT_FILTER);
			reset({
				period: MY_WITHDRAWALS_DEFAULT_FILTER.period,
				paymentMethodId: MY_WITHDRAWALS_DEFAULT_FILTER.paymentMethodId,
				transactionStatusId:
					MY_WITHDRAWALS_DEFAULT_FILTER.transactionStatusId,
			});
		}

		return () => {
			disposeCurrentWithdrawalState();
			setDefaultFilterData();
		};
	}, [isStoreInitialized]);

	const onSubmit = (data: RawFilterData) => {
		setRawFilterData({
			...data,
		});
	};

	const onPageChange = (pageNumber: number) => {
		window.scrollTo(0, 0);
		setRawFilterData({
			...rawData,
			pageNumber: pageNumber,
		});
	};

	return (
		<>
			<FormProvider {...methods}>
				<form
					className="card--primary"
					onSubmit={handleSubmit(onSubmit)}
					data-at="at-MyWithdrawalsBody"
				>
					<div className="card--primary__header">
						<h1 className="card--primary__title title--primary">
							{t<string>("WITHDRAWAL.TITLE")}
						</h1>
					</div>

					<WithdrawalTabSwitcher />

					<div className="card--primary__body">
						<WithdrawalPeriodsFilterSection />

						<WithdrawalCustomPeriodInput />

						<WithdrawalPaymentMethodFilterSection />

						<WithdrawalTransactionStatusFilterSection />

						<Button
							type="submit"
							className="btn btn--base btn--accent"
							btnText={t<string>("COMMON.SUBMIT_BUTTON")}
							disabled={isLoadingProcess}
						></Button>
					</div>
				</form>
			</FormProvider>

			<MyWithdrawalsTable />

			<Pager
				pageNumber={pageNumber}
				pageSize={pageSize}
				totalItems={totalItems}
				onPageChange={onPageChange}
				withNumberInput
			/>

			<TransactionCancelConfirmModal />
		</>
	);
});
